import { useState } from 'react';

// components
import Link from '@/components/utilities/SmartLink';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger
} from '@/components/ui/navigation-menu';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import VisibleLoading from '@/components/utilities/VisibleLoading';
import ComponentsRender from '@/components/ComponentsRender';
import ChevronRight from '@/components/ui/icons/ChevronRight';
import Logo from '@/components/ui/logo';

// constants
import { NAVIGATION } from '@/components/components';

// hooks
import { useScrollSensor } from '@/components/utilities/ScrollSensor';
import { useAppContext } from '@/components/utilities/AppContext';

const Navigation = ({ navigation }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { variant } = useScrollSensor();
  const { locale } = useAppContext();
  const isLight = variant === 'dark';
  const homeHref = locale && locale !== 'cs' ? `/${locale}` : '/';

  return (
    <VisibleLoading delay={100} onload={true}>
      {({ ref, className }) => (
        <>
          <div
            className={`fixed left-1/2 z-50 mx-auto mt-4 hidden min-h-10 -translate-x-1/2 rounded-[20px] border border-transparent pr-2 pl-4 backdrop-blur-md md:w-[90vw] lg:block lg:h-15 lg:rounded-[40px] lg:transition xl:w-(--container-width) ${className || ''}`.trim()}
          />
          <div
            ref={ref}
            className={`lg:max-h-auto fixed left-1/2 z-50 mx-auto mt-4 flex max-h-[90vh] min-h-10 w-[90vw] -translate-x-1/2 justify-stretch rounded-[20px] border px-4 backdrop-blur-md duration-300 lg:h-15 lg:items-center lg:rounded-[40px] lg:pr-2 lg:backdrop-blur-none lg:transition xl:w-(--container-width) ${isLight ? 'border-[rgba(255,255,255,0.2)] text-white' : 'border-[rgba(0,0,0,0.2)] text-black'} ${className || ''}`.trim()}
          >
            <Sheet>
              <div className="flex w-full flex-1 flex-wrap items-center justify-between py-2">
                <Link href={homeHref} aria-label="Leadhub — úvodní stránka">
                  <Logo className="h-[20px] xl:h-[28px]" />
                </Link>

                <SheetTrigger aria-label="Otevřít hlavní menu">
                  <div className="lg:hidden">
                    <Icon type="menu" width={22} height={22} />
                  </div>
                </SheetTrigger>

                <div className="hidden lg:flex">
                  <NavigationMenu viewport={false}>
                    <NavigationMenuList>
                      {(navigation.items || []).map((item) =>
                        item.isSimpleLink ? (
                          <MenuItemLink key={item.id} {...item} />
                        ) : (
                          <MenuItemContent key={item.id} {...item} />
                        )
                      )}
                    </NavigationMenuList>
                  </NavigationMenu>
                </div>

                <SheetContent
                  side="left"
                  className="h-[100vh] w-full gap-0 overflow-auto border-none bg-white/80 text-black backdrop-blur-md lg:hidden"
                  aria-describedby="Mobile Navigation Menu"
                >
                  <SheetHeader className="border-b border-black/10 px-4 py-5">
                    <SheetTitle>
                      <Link href={homeHref}>
                        <Logo className="h-6" />
                      </Link>
                    </SheetTitle>
                  </SheetHeader>

                  <div className="p-4 pb-60">
                    {(navigation.items || []).map((item) =>
                      item.isSimpleLink ? (
                        <MenuItemLinkMobile key={item.id} {...item} />
                      ) : (
                        <MenuItemContentMobile isMobile={true} key={item.id} {...item} />
                      )
                    )}

                    <div className="mt-8 flex flex-col gap-3">
                      {(navigation.buttons || []).map((button) => (
                        <Button
                          key={button.id}
                          variant={button.variant}
                          variantReverse={isLight}
                          size={button.size}
                          asChild={true}
                        >
                          <Link href={button.url || ''}>
                            <Icon type={button.icon} /> {button.label}
                          </Link>
                        </Button>
                      ))}
                    </div>
                  </div>
                </SheetContent>

                <div className="hidden gap-3 lg:flex">
                  {(navigation.buttons || []).map((button) => (
                    <Button
                      key={button.id}
                      variant={button.variant}
                      variantReverse={!isLight}
                      size={button.size}
                      asChild={true}
                    >
                      <Link href={button.url || ''}>
                        <Icon type={button.icon} /> {button.label}
                      </Link>
                    </Button>
                  ))}
                </div>
              </div>
            </Sheet>
          </div>
        </>
      )}
    </VisibleLoading>
  );
};
export default Navigation;

const MenuItemLink = ({ label, link }) => {
  const { locale } = useAppContext();
  const homeHref = locale && locale !== 'cs' ? `/${locale}` : '/';

  return (
    <NavigationMenuItem className="hover:text-brand-400 font-medium">
      <NavigationMenuLink asChild={true}>
        <Link href={link || homeHref}>
          <div className="flex min-h-[60px] items-center">{label}</div>
        </Link>
      </NavigationMenuLink>
    </NavigationMenuItem>
  );
};

const MenuItemContent = ({ label, navigationContent }) => {
  return (
    <NavigationMenuItem className="hover:text-brand-400">
      <NavigationMenuTrigger className="min-h-[60px] bg-transparent transition-none duration-300">
        {label}
      </NavigationMenuTrigger>
      <NavigationMenuContent className="-translate-x-1/3 -translate-y-1 border-black/20 shadow-none!">
        <div className="flex gap-8 gap-20 p-8 text-black">
          {(navigationContent || []).map((column) => (
            <div key={column.id} className="flex min-w-[310px] flex-col gap-8">
              {(column.navigationColumnContent || []).map((component) => (
                <ComponentsRender
                  key={`${component.id}_${component.__typename}`}
                  type={NAVIGATION}
                  component={component}
                />
              ))}
            </div>
          ))}
        </div>
      </NavigationMenuContent>
    </NavigationMenuItem>
  );
};

const MenuItemLinkMobile = ({ label, link }) => {
  const { locale } = useAppContext();
  const homeHref = locale && locale !== 'cs' ? `/${locale}` : '/';

  return (
    <div className="font-medium">
      <Link href={link || homeHref}>
        <div className="flex min-h-[60px] items-center text-xl">{label}</div>
      </Link>
    </div>
  );
};

const MenuItemContentMobile = ({ label, navigationContent }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="font-medium">
      <div className="flex min-h-[60px] items-center text-xl" onClick={() => setIsOpen((v) => !v)}>
        {label}

        <div className={`ml-1 rotate-90`}>
          <ChevronRight />
        </div>
      </div>

      <div
        className={`flex flex-col gap-8 px-2 py-4 text-black lg:flex-row lg:gap-20 lg:p-8 ${isOpen ? '' : 'hidden'}`}
      >
        {(navigationContent || []).map((column) => (
          <div key={column.id} className="flex flex-col gap-8">
            {(column.navigationColumnContent || []).map((component) => (
              <ComponentsRender
                key={`${component.id}_${component.__typename}`}
                type={NAVIGATION}
                component={component}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
